import './LogbookWhiteList.css';

import React, { useEffect, useState } from 'react';
import { AddModerator, Lock, LockOpen, RemoveModerator } from '@mui/icons-material';
import UserOwner from '../../../b2b-common/UserOwner';
import BooleanIconToggle from '../../../b2b-common/BooleanIconToggle';
import { useLoginContext } from '../../../../App';
import { fetchUsersHavingRoles } from '../../../../services/b2b-users-apiService';
import { useContentContext } from '../../../layout/Layout';
import { showNotification } from '../../../b2b-common/Notification';
import ActionableMembersTable from './ActionableMembersTable';
import { addMemberToWhitelist, changeLogbookOwner, protectLogbook, removeMemberFromWhitelist, unprotectLogbook } from '../../../../services/b2b-logbook-apiServices';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';

const WhiteListEnabler = ({ logbook, onChange }) => {

    const { t } = useB2BTranslation('b2b-logbook');

    return (
        <div className="logbook-whitelist-enabler">
            <BooleanIconToggle
                value={logbook.logBookProtected}
                trueIcon={<Lock />}
                falseIcon={<LockOpen />}
                onChange={onChange}
                className={'logbook-whitelist-enabler-toggle'}
            />
            <div className="logbook-whitelist-enabler-text">
                {logbook.logBookProtected ? t('Whitelist is Enabled') : t('Whitelist is Disabled')}
            </div>
        </div>
    );
};

const LogbookWhiteList = ({ logbook, setLogbook }) => {

    const { t } = useB2BTranslation('b2b-logbook');
    const { setNotification } = useContentContext();
    const { loginData } = useLoginContext();

    const [currentLogbook, setCurrentLogbook] = useState(logbook);
    const [organizationUsers, setOrganizationUsers] = useState([]);
    const [whitelistedUsers, setWhitelistedUsers] = useState([]);
    const [eligibleUsers, setEligibleUsers] = useState([]);

    useEffect(() => {
        const fetchOrganizationUsers = async () => {
            try {
                const allPotentialUsers = await fetchUsersHavingRoles(loginData.accessToken, loginData.loggedOrganizationUuid, ['ADMIN', 'EDITOR', 'USER']);
                setOrganizationUsers(allPotentialUsers);
            } catch (error) {
                console.error('Error fetching eligible users', error);
                showNotification(setNotification, t('Error fetching eligible users: ') + error.response.statusText, 'error', 5000);
            }
        };

        setCurrentLogbook(logbook);
        if (!organizationUsers || organizationUsers.length === 0) {
            fetchOrganizationUsers();
        }
    }, [logbook, organizationUsers, loginData, setNotification, t]);

    useEffect(() => {
        if (organizationUsers && organizationUsers.length > 0) {
            if (currentLogbook.logBookProtected) {
                const whitelisted = currentLogbook.whitelistUsers.map(wlUser => wlUser.organizationMember);
                setWhitelistedUsers(whitelisted);
            }
        }
    }, [organizationUsers, currentLogbook]);

    useEffect(() => {
        if (organizationUsers && organizationUsers.length > 0) {
            if (currentLogbook.logBookProtected && whitelistedUsers) {
                const eligibles = organizationUsers.filter(user => !whitelistedUsers.find(whitelisted => whitelisted.userId === user.userId));
                setEligibleUsers(eligibles.filter(user => user.user.userUuid !== currentLogbook.logBookOwnerUUID));
            }
        }
    }, [currentLogbook.logBookOwnerUUID, currentLogbook.logBookProtected, organizationUsers, whitelistedUsers]);

    const handleChangeProtected = (value) => {
        if (value) {
            try {
                protectLogbook(loginData.accessToken, currentLogbook.logBookId);
                showNotification(setNotification, t('Logbook protected successfully.'), 'success', 3000);
                setCurrentLogbook({ ...currentLogbook, logBookProtected: value });
                setLogbook({ ...currentLogbook, logBookProtected: value });
            } catch (error) {
                console.error('Error protecting logbook:', error);
                showNotification(setNotification, t('Error protecting logbook: ') + error.response.statusText, 'error', 5000);
            }
        } else {
            try {
                unprotectLogbook(loginData.accessToken, currentLogbook.logBookId);
                showNotification(setNotification, t('Logbook unprotected successfully.'), 'success', 3000);
                setCurrentLogbook({ ...currentLogbook, logBookProtected: value });
                setLogbook({ ...currentLogbook, logBookProtected: value });
            } catch (error) {
                console.error('Error unprotecting logbook:', error);
                showNotification(setNotification, t('Error unprotecting logbook: ') + error.response.statusText, 'error', 5000);
            }
        }
    };

    const handleAddToWhitelist = async (userId) => {
        const userUUID = organizationUsers.find(user => user.userId === userId).user.userUuid;
        if (!userUUID) {
            showNotification(setNotification, t('Error adding user to whitelist: User not found in eligible members of the organization.'), 'error', 5000);
            return;
        }
        try {
            const updatedWhitelist = await addMemberToWhitelist(loginData.accessToken, currentLogbook.logBookId, userUUID);
            setLogbook({ ...currentLogbook, whitelistUsers: updatedWhitelist });
            setCurrentLogbook({ ...currentLogbook, whitelistUsers: updatedWhitelist });
            showNotification(setNotification, t('User added to whitelist successfully.'), 'success', 3000);
        } catch (error) {
            console.error('Error adding user to whitelist:', error);
            showNotification(setNotification, t('Error adding user to whitelist: ') + error.response.statusText, 'error', 5000);
        }
    }

    const handleRemoveFromWhitelist = async (userId) => {
        // console.log('Remove from whitelist:', userId);
        // console.log('Whitelisted users:', whitelistedUsers);
        const userUUID = whitelistedUsers.find(user => user.userId === userId).user.userUuid;
        if (!userUUID) {
            showNotification(setNotification, t('Error removing user from whitelist: User not found in whitelisted members.'), 'error', 5000);
            return;
        }
        try {
            const updatedWhitelist = await removeMemberFromWhitelist(loginData.accessToken, currentLogbook.logBookId, userUUID);
            setLogbook({ ...currentLogbook, whitelistUsers: updatedWhitelist });
            setCurrentLogbook({ ...currentLogbook, whitelistUsers: updatedWhitelist });
            showNotification(setNotification, t('User removed from whitelist successfully.'), 'success', 3000);
        } catch (error) {
            console.error('Error removing user from whitelist:', error);
            showNotification(setNotification, t('Error removing user from whitelist: ') + error.response.statusText, 'error', 5000);
        }
    }

    const handleLogbookOwnerChange = async (newOwner) => {
        try {
            const newOwnership = await changeLogbookOwner(loginData.accessToken, currentLogbook.logBookId, newOwner.user.userUuid);
            setLogbook({ ...currentLogbook, logBookOwnerUUID: newOwnership.user.userUuid });
            setCurrentLogbook({ ...currentLogbook, logBookOwnerUUID: newOwnership.user.userUuid });
            showNotification(setNotification, t('Logbook owner changed successfully.'), 'success', 3000);
        } catch (error) {
            console.error('Error changing logbook owner:', error);
            showNotification(setNotification, t('Error changing logbook owner: ') + error.response.statusText, 'error', 5000);
        }
    }

    return (
        <div className="logbook-whitelist">
            <div className="logbook-whitelist-content">
                <div className="logbook-owner-whitelist-enabler">
                    <UserOwner userUUID={currentLogbook?.logBookOwnerUUID}
                        className="logbook-whitelist-user-owner"
                        onOwnerChange={handleLogbookOwnerChange}
                    />
                    <WhiteListEnabler logbook={currentLogbook} onChange={handleChangeProtected} />
                </div>
                {currentLogbook.logBookProtected && (
                    <div className="logbook-whitelist-list-management">
                        <ActionableMembersTable
                            bannerMsg={'Whitelisted members:'}
                            ActionIcon={RemoveModerator}
                            actionLabel={t('Remove from whitelist')}
                            members={whitelistedUsers}
                            onActionOnMember={handleRemoveFromWhitelist}
                            className="logbook-whitelist-list-included"
                        />
                        <ActionableMembersTable
                            bannerMsg={'Eligible members:'}
                            ActionIcon={AddModerator}
                            actionLabel={t('Add to whitelist')}
                            members={eligibleUsers}
                            onActionOnMember={handleAddToWhitelist}
                            className="logbook-whitelist-list-excluded"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default LogbookWhiteList;